import { Stack, Typography, TextField, Grid, Paper, MenuItem, Button, IconButton, CircularProgress } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { useEffect, useState } from "react";
import { toast } from 'react-hot-toast';
import axios from "axios";
import { useSelector } from "react-redux"; // Assuming you might need user state later

import { CONFIG } from "../../../../../config";
import { getAxiosErrorMessage } from "../../../../../utility/axios-error-message";

// ICONS
import EditIcon from '@mui/icons-material/Edit';
import DoneIcon from '@mui/icons-material/Done';

const API_HOST = CONFIG.modules["crm"].host; // Assuming CRM module host

// Define options for motherStatus
const motherStatusOptions = [
    { value: "Delivered", label: "Delivered" },
    { value: "Currently Expecting", label: "Currently Expecting" },
    { value: "NA", label: "N/A" }
];

const pregnancyMonth = [
    "Delivered",
    "One",
    "Two",
    "Three",
    "Four",
    "Five",
    "Six",
    "Seven",
    "Eight",
    "Nine",
    "Ten",
];

const trimesterCount = ["Delivered", "One", "Two", "Three", "Four"];

const PregnancyDetails = ({ caseData, updateCaseData }) => {
    // const { user } = useSelector((state) => state.user || {}); // Example if user state is needed

    const [updateMode, setUpdateMode] = useState(false);
    const [updateLoading, setUpdateLoading] = useState(false);

    const { register, handleSubmit, formState: { errors }, setValue, control, getValues } = useForm({
        // Add resolver later if needed: yupResolver(pregnancyDetailsSchema)
        defaultValues: {
            motherStatus: caseData?.motherStatus || "", // Assuming 'motherStatus' field exists in caseData
        }
    });

    // Effect to update form values if caseData changes
    useEffect(() => {
        if (caseData) {
            setValue("motherStatus", caseData.motherStatus || "");
        }
    }, [caseData, setValue]);

    const handleUpdate = async (data) => {
        // Check if the value has changed
        if (data.motherStatus === (caseData?.motherStatus || "")) {
            toast.error("No changes to update", {
                duration: 1200,
                icon: "🔴",
                style: { borderRadius: "10px", background: "#333", color: "#fff" },
                position: "top-center",
            });
            return;
        }

        setUpdateLoading(true);
        try {
            // Prepare payload - adjust field name if needed by API
            const payload = {
                motherStatus: data.motherStatus,
            };

            const response = await axios.put(`${API_HOST}/case/${caseData?.motherID}`, payload); // Example endpoint

            if (response.status === 200) {
                toast.success("Pregnancy details updated successfully", {
                    duration: 1200,
                    icon: "🟢",
                });
                updateCaseData(response.data.case); // Update local state
            } else {
                toast.error("Failed to update pregnancy details", { duration: 1200, icon: "🔴" });
            }

        } catch (error) {
            console.error("Error updating pregnancy details:", error);
            toast.error(getAxiosErrorMessage(error), {
                duration: 1200,
                icon: "🔴",
                style: { borderRadius: "10px", background: "#333", color: "#fff" },
                position: "top-center",
            });
        } finally {
            setUpdateLoading(false);
        }
    };

    return (
        <Stack direction={"column"} spacing={1} px={2} py={1}>
            <Stack component={Paper} elevation={2} direction={"column"} spacing={1} px={2} py={2}>
                <Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"}>
                    <Typography variant="h6" gutterBottom>
                        Pregnancy Details
                    </Typography>
                    <Stack>
                        <IconButton
                            disabled={updateLoading}
                            color="primary"
                            onClick={() => setUpdateMode(!updateMode)}
                        >
                            {updateMode ? <DoneIcon /> : <EditIcon />}
                        </IconButton>
                    </Stack>
                </Stack>

                <Stack component={"form"} onSubmit={handleSubmit(handleUpdate)} direction={"column"} spacing={2}>
                    <Grid container rowGap={2} columnSpacing={{ xs: 0, md: 2 }} sx={{ pr: { xs: 0, md: 2 } }}>
                        <Grid item xs={12} md={4}> {/* Adjust grid size as needed */}
                            <Controller
                                name="motherStatus"
                                control={control}
                                render={({ field }) => (
                                    <TextField
                                        select
                                        fullWidth
                                        label="Mother Status"
                                        {...field}
                                        error={!!errors.motherStatus}
                                        helperText={errors.motherStatus?.message}
                                        disabled={!updateMode}
                                    >
                                        {motherStatusOptions.map((option) => (
                                            <MenuItem key={option.value} value={option.value}>
                                                {option.label}
                                            </MenuItem>
                                        ))}
                                    </TextField>
                                )}
                            />
                        </Grid>
                        {/* Add more fields here within Grid items if needed later */}
                    </Grid>
                    {updateMode && (
                        <Stack direction={"row"} justifyContent={"flex-end"}>
                            <Button type="submit" variant="contained" color="primary" disabled={updateLoading} sx={{ height: "100%" }}>
                                {updateLoading ? <CircularProgress size={20} /> : "Update"}
                            </Button>
                        </Stack>
                    )}
                </Stack>
            </Stack>
        </Stack>
    );
};

/**
 * PregnancyDetails component renders a section for editing mother status in a case.
 *
 * @returns {React.ReactElement} PregnancyDetails component
 */
/**
 * Default export of PregnancyDetails component
 *
 * @type {React.ReactElement}
 */
/**
 * Default export of PregnancyDetails component.
 *
 * @type {React.ReactElement}
 */
export default PregnancyDetails;